import { Observable, defer, merge } from "rxjs"
import { switchMap, filter, debounceTime, delayWhen } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_SUMMARY_REPORT_DEFAULT_FILTER,
    setSummaryReportDefaultFilter,
} from "../../../filters/actions"
import {
    fetchNetCollectedBreakdownByPaymentTypeDataFailure,
    fetchNetCollectedBreakdownByPaymentTypeDataSuccess,
    fetchTipsAndGratuityByAssignmentDataSuccess,
} from "../../../../api/actions"
import { getHttpHeaders } from "../../../auth/selectors"
import { clearExtensiveReportsData, EXTENSIVE_REPORTS_AGENDA_MOUNTED, extensiveReportsAgendaMounted } from "./actions"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../customers/customersTable/epics"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { areAnyLocationsSelected } from "../../../filters/selectors"
import { fetchNetCollectedBreakdownByPaymentTypeReportData, fetchTipsAndGratuityReportData } from "./request"
import { isExtensiveReportsAgendaOpened, isSummaryAgendaOpened } from "../../../data/selectors"
import { getSummaryQueryApiParamNew } from "../summary/selectors"
import { Action, StateObject } from "../../../redux/types"

export const summaryReportAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isExtensiveReportsAgendaOpened(deps.getState())),
        switchMap(() => [extensiveReportsAgendaMounted()])
    )

export const summaryAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(EXTENSIVE_REPORTS_AGENDA_MOUNTED),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => [setSummaryReportDefaultFilter()])
    )

export const fetchSummaryReportRequestedLoading = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(EXTENSIVE_REPORTS_AGENDA_MOUNTED, REFETCH_DATA)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "summaryReport" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isExtensiveReportsAgendaOpened(deps.getState())),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                return []
            } else {
                return [clearExtensiveReportsData()]
            }
        })
    )

export const summaryReportFilterChangedLoadings = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_SUMMARY_REPORT_DEFAULT_FILTER, REFETCH_DATA)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "summaryReport" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isExtensiveReportsAgendaOpened(deps.getState())),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                return [createLoadingStarted("netCollectedBreakdownByPaymentTypeDataLoading")]
            } else {
                return [createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading")]
            }
        })
    )

export const summaryReportFilterChanged = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_SUMMARY_REPORT_DEFAULT_FILTER, REFETCH_DATA)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "summaryReport" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isSummaryAgendaOpened(deps.getState())),
        debounceTime(1000),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                return merge(
                    defer(() =>
                        fetchNetCollectedBreakdownByPaymentTypeReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result => {
                            return result
                                ? [
                                      fetchNetCollectedBreakdownByPaymentTypeDataSuccess(result),
                                      createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading"),
                                  ]
                                : [
                                      fetchNetCollectedBreakdownByPaymentTypeDataFailure(),
                                      createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading"),
                                  ]
                        })
                    ),
                    defer(() =>
                        fetchTipsAndGratuityReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result => {
                            return result
                                ? [
                                      fetchTipsAndGratuityByAssignmentDataSuccess(result),
                                      createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading"),
                                  ]
                                : [
                                      fetchNetCollectedBreakdownByPaymentTypeDataFailure(),
                                      createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading"),
                                  ]
                        })
                    )
                )
            } else {
                return [createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading")]
            }
        })
    )

export default [
    summaryReportFilterChangedLoadings,
    summaryReportFilterChanged,
    fetchSummaryReportRequestedLoading,
    summaryAgendaMountedEpics,
    summaryReportAccountSwitch,
]
